import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { Buyer } from '@ps/core-models';
import { PlatformSwitcherRouteKey } from '@ps/platform-switcher-data-access';
import { Nullable } from '@ps/utils';
import { Survey, TrackerConfig } from '@ps/survey-utils';
import { SurveyChildKey } from '../survey-creation-shared-state.model';

/**
 * Actions **shared across the whole survey-creation workflow**.
 * Anything that only one stage can dispatch belongs to that stage's own
 * group instead (e.g. creating a survey is create-only, so it lives on `SurveyCreationAudienceActions`).
 */
export const SurveyCreationCoreActions = createActionGroup({
  source: 'Survey Creation Core',
  events: {
    'Set Active Child Key': props<{
      activeChildKey: Nullable<SurveyChildKey>;
    }>(),
    // The "save" family stays together: one pipeline, and between them the variants are dispatched from create, edit and launch alike.
    // `silent` marks a save the buyer did not ask for — switching locale
    // persists the locale being left, and that write announces nothing.
    'Save Survey': props<{ silent?: boolean }>(),
    'Save And Clone Survey': emptyProps(),
    'Save And Create New': emptyProps(),
    'Save And Launch': emptyProps(),
    // Saves and sets the survey live in the same write — a Specific Audience
    // channel is launched from its Edit Survey page this way.
    'Save As Live': emptyProps(),
    'Save And Edit Audience': emptyProps(),
    // `updateSupplier` is the launched-survey pro-rata choice sent with the write.
    'Save And Open Manage Survey': props<{ updateSupplier: boolean }>(),
    'Save And Test': emptyProps(),
    'Save And Exit': emptyProps(),
    'Save Survey And Disconnect Impersonation': emptyProps(),
    'Save Survey And Switch Impersonated Buyer': props<{ buyer: Buyer }>(),
    'Save Survey And Switch To Legacy': props<{
      routeKey: PlatformSwitcherRouteKey;
      params: Record<string, string | number>;
    }>(),
    'Discard Survey Changes': emptyProps(),
    'Reset Active Survey Id': emptyProps(),
    // Dispatched from the Tracker Configuration Drawer's confirm. Synchronous/pure write, no API event needed.
    'Setup Tracker Config': props<{ config: TrackerConfig }>(),
    'Reset Tracker Config': emptyProps(),
    Reset: emptyProps(),
  },
});

export const SurveyCreationCoreApiActions = createActionGroup({
  source: 'Survey Creation Core API',
  events: {
    'Save Survey Success': props<{ survey: Survey }>(),
    'Save Survey Failure': props<{ error: string }>(),
    'Clone Survey Success': props<{ survey: Survey }>(),
    'Clone Survey Failure': props<{ error: string }>(),
    'Load Root Survey For Edit Success': props<{ survey: Survey }>(),
    'Load Root Survey For Edit Failure': props<{ error: string }>(),
    'Load Active Survey For Edit Success': props<{ survey: Survey }>(),
    'Load Active Survey For Edit Failure': props<{ error: string }>(),
    'Save Survey And Exit Success': props<{ survey: Survey }>(),
    'Save Survey And Disconnect Impersonation Success': props<{
      survey: Survey;
    }>(),
    'Save Survey And Switch Impersonated Buyer Success': props<{
      survey: Survey;
      buyer: Buyer;
    }>(),
    'Save Survey And Switch To Legacy Success': props<{
      survey: Survey;
      routeKey: PlatformSwitcherRouteKey;
      params: Record<string, string | number>;
    }>(),
  },
});

export const SurveyCreationCoreChildrenActions = createActionGroup({
  source: 'Survey Creation Core Children',
  events: {
    'Switch Child': props<{ surveyId: number }>(),
  },
});

export const SurveyCreationCoreChildrenApiActions = createActionGroup({
  source: 'Survey Creation Core Children API',
  events: {
    'Load Child Surveys Success': props<{ surveys: Survey[] }>(),
    'Load Child Surveys Failure': props<{ error: string }>(),
  },
});
